"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Acl = void 0;
var Permission_1 = require("./intersection/Permission");
/**
 * Creates a new Acl object, with an empty rule set for an object
 */
var Acl = /** @class */ (function () {
    function Acl() {
        /**
         * The read permission of the object
         */
        this.read = new Permission_1.Permission();
        /**
         * The write permission of the object
         */
        this.write = new Permission_1.Permission();
    }
    /**
     * Removes all acl rules, read and write access is public afterwards
     *
     * @return
     * */
    Acl.prototype.clear = function () {
        this.read.clear();
        this.write.clear();
    };
    /**
     * Copies permissions from another ACL
     *
     * @param acl The ACL to copy from
     * @return
     */
    Acl.prototype.copy = function (acl) {
        this.read.copy(acl.read);
        this.write.copy(acl.write);
        return this;
    };
    /**
     * Gets whenever all users and roles have the permission to read the object
     *
     * @return <code>true</code> If public access is allowed
     */
    Acl.prototype.isPublicReadAllowed = function () {
        return this.read.isPublicAllowed();
    };
    /**
     * Sets whenever all users and roles should have the permission to read the object
     *
     * Note: All other allow read rules will be removed.
     *
     * @return
     * */
    Acl.prototype.setPublicReadAllowed = function () {
        return this.read.setPublicAllowed();
    };
    /**
     * Checks whenever the user or role is explicit allowed to read the object
     *
     * @param userOrRole The user or role to check for
     * @return <code>true</code> if read access is explicitly allowed for the given user or role
     */
    Acl.prototype.isReadAllowed = function (userOrRole) {
        return this.read.isAllowed(userOrRole);
    };
    /**
     * Checks whenever the user or role is explicit denied to read the object
     *
     * @param userOrRole The user or role to check for
     * @return <code>true</code> if read access is explicitly denied for the given user or role
     */
    Acl.prototype.isReadDenied = function (userOrRole) {
        return this.read.isDenied(userOrRole);
    };
    /**
     * Allows the given user or rule to read the object
     *
     * @param userOrRole The user or role to allow
     * @return this acl object
     */
    Acl.prototype.allowReadAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.read).allowAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * Denies the given user or rule to read the object
     *
     * @param userOrRole The user or role to deny
     * @return this acl object
     */
    Acl.prototype.denyReadAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.read).denyAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * Deletes any read allow/deny rule for the given user or role
     *
     * @param userOrRole The user or role
     * @return this acl object
     */
    Acl.prototype.deleteReadAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.read).deleteAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * Gets whenever all users and roles have the permission to write the object
     *
     * @return <code>true</code> If public access is allowed
     */
    Acl.prototype.isPublicWriteAllowed = function () {
        return this.write.isPublicAllowed();
    };
    /**
     * Sets whenever all users and roles should have the permission to write the object
     *
     * Note: All other allow write rules will be removed.
     *
     * @return
     * */
    Acl.prototype.setPublicWriteAllowed = function () {
        return this.write.setPublicAllowed();
    };
    /**
     * Checks whenever the user or role is explicit allowed to write the object
     *
     * @param userOrRole The user or role to check for
     * @return <code>true</code> if write access is explicitly allowed for the given user or role
     */
    Acl.prototype.isWriteAllowed = function (userOrRole) {
        return this.write.isAllowed(userOrRole);
    };
    /**
     * Checks whenever the user or role is explicit denied to write the object
     *
     * @param userOrRole The user or role to check for
     * @return <code>true</code> if write access is explicitly denied for the given user or role
     */
    Acl.prototype.isWriteDenied = function (userOrRole) {
        return this.write.isDenied(userOrRole);
    };
    /**
     * Allows the given user or rule to write the object
     *
     * @param userOrRole The user or role to allow
     * @return this acl object
     */
    Acl.prototype.allowWriteAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.write).allowAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * Denies the given user or rule to write the object
     *
     * @param userOrRole The user or role to deny
     * @return this acl object
     */
    Acl.prototype.denyWriteAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.write).denyAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * Deletes any write allow/deny rule for the given user or role
     *
     * @param userOrRole The user or role
     * @return this acl object
     */
    Acl.prototype.deleteWriteAccess = function () {
        var _a;
        var userOrRole = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            userOrRole[_i] = arguments[_i];
        }
        (_a = this.write).deleteAccess.apply(_a, userOrRole);
        return this;
    };
    /**
     * A JSON representation of the set of rules
     *
     * @return
     */
    Acl.prototype.toJSON = function () {
        return {
            read: this.read.toJSON(),
            write: this.write.toJSON(),
        };
    };
    /**
     * Sets the acl rules form JSON
     *
     * @param json The json encoded acls
     * @return
     */
    Acl.prototype.fromJSON = function (json) {
        this.read.fromJSON(json.read || {});
        this.write.fromJSON(json.write || {});
    };
    return Acl;
}());
exports.Acl = Acl;
//# sourceMappingURL=data:application/json;base64,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